import { DataGrid, type Column, type Group } from '../../platform/primitives/DataGrid'
import { Panel } from '../../platform/primitives/Panel'
import { CategoryIcon } from '../../platform/icons/CategoryIcon'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { useHitTarget } from '../../platform/responsive/hitTarget'
import { CATEGORY_TYPE_LABEL, type Category } from './categoryTree'
import type { CategoryView } from './useCategoryView'
import './categories.css'

/**
 * The categories management table — the design intake's Q3.
 *
 * The shape is the design's: five columns, a row that discloses a sunk detail
 * panel holding its subcategories and the actions that belong to the parent,
 * over a search pill, a sort segmented control and Expand all.
 *
 * **Where a column has no data, it says so — it does not invent one.**
 * Auto-rules needs a per-category rule count (#287/#288) and does not exist,
 * so that column renders an em-dash, which claims nothing. Status is real but
 * only two-thirds real: `is_active` gives Active and Archived today, while
 * `Unused` needs the lifetime usage aggregate (intake §5 A) and is therefore
 * never shown. A category with no transactions currently reads Active, and
 * that is the honest answer to "is this category live", just not the useful
 * one.
 *
 * The sort offers only the orders it can actually compute — see `SORTS`.
 * Drawing the design's other two as dimmed segments was tried and was wrong:
 * a control that does nothing reads as broken, not as forthcoming.
 *
 * **88 rows, not the design's 14** (Q1), which is why the filter carries more
 * weight here than in the mock.
 */




/** A bin, at 24 units, drawn to sit beside the pencil at the same weight. */
function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 7h16M10 7V5h4v2M6 7l1 13h10l1-13M10 11v6M14 11v6"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** A pencil, at 24 units, matching the design's own path. */
function PencilIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 20h4l10-10-4-4L4 16z"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
      />
    </svg>
  )
}

export function CategoryTable({
  view,
  onEdit,
  onDelete,
  onActivate,
}: {
  /**
   * The filtered, sorted tree and its open-row state, owned by the ROUTE.
   *
   * This component used to hold all of it, which forced the toolbar to live
   * here too — and this component renders only inside `<Async>`'s success
   * branch, so the filter, the sort and "New category" all disappeared while
   * the list was loading and on an error. `useCategoryView` moved the state
   * up so one toolbar renders unconditionally; see that hook's docblock for
   * the two worse arrangements this replaced.
   */
  view: CategoryView
  onEdit: (category: Category) => void
  /** Ask to delete a row. The ROUTE owns the confirm dialog and the toast (#376), so this table stays presentational and `Probe.tsx` needs no provider. */
  onDelete: (category: Category) => void
  onActivate: (category: Category) => void
  /**
   * The caller's `most_used` ranking — **a prop, not a hook, and that
   * distinction was got wrong first.**
   *
   * It was briefly read here with `useMostUsed()`, which
   * looked free: the route already holds that cache entry, so it is one more
   * observer rather than one more request. It is not free, for two reasons.
   * This component takes its rows as a prop and is therefore presentational,
   * and presentational components in this app do not fetch. And `Probe.tsx`
   * renders it against a FIXED fixture precisely so two screenshots differ
   * only for the reason under test — a hook here mounts a live observer
   * behind fixture rows, so the sweeps would photograph a real account's
   * ranking over invented categories.
   *
   * Defaulted, so a caller without one renders no Most used segment. (A
   * `pack` prop sat beside it until #447: backend #401 removed the pack
   * metadata it carried, and each row's icon now arrives as its own
   * `icon_url`.)
   */
  mostUsed?: readonly string[]
}) {
  const target = useHitTarget('page')
  // M11-R21: below 640px the five-column table cannot hold. Measured, not
  // assumed — the browser sweep found `.categories__cell-name` at 0x44 at
  // 375px, i.e. the category NAME collapsed to nothing, because five tracks
  // do not fit and `minmax(0, …)` lets the first one shrink to zero rather
  // than overflow. `useBreakpoint()`, never a media query
  // (`single-source.test.ts` forbids one in any stylesheet).
  const phone = useBreakpoint() === 'phone'

  const { query, segments, open, toggle, childrenById } = view

  const columns: Column<Category>[] = [
    {
      key: 'category',
      header: 'Category',
      // `minmax(0, …)` rather than a bare `fr` — an `fr` track takes
      // min-content as its floor, so a long name refuses to shrink and the
      // grid overflows below about 360px. DataGrid's own docblock measured it.
      width: 'minmax(0, 1.6fr)',
      render: (c) => (
        <span className="categories__cell">
          <span className="categories__glyph">
            <CategoryIcon category={c} />
          </span>
          <button
            type="button"
            className="categories__cell-name hoverable"
            // Both axes, not just height: the sweep measures width too, and a
            // name in a squeezed track collapsed below the floor at 320px.
            style={{ minHeight: target, minWidth: target }}
            onClick={() => onActivate(c)}
          >
            {c.name}
          </button>
        </span>
      ),
    },
    {
      key: 'subs',
      header: 'Subs',
      width: 'minmax(0, 0.7fr)',
      // The word, not the number, at zero — the design's own rule. "0" reads
      // as a count that might grow; "None" reads as a category that does not
      // work that way.
      render: (c) => {
        const n = childrenById.get(c.id)?.length ?? 0
        return <span className="categories__meta">{n === 0 ? 'None' : String(n)}</span>
      },
    },
    {
      key: 'rules',
      header: 'Auto-rules',
      width: 'minmax(0, 1fr)',
      // An em-dash, not "No rules". The chip would assert a count of zero,
      // which nobody has measured — there is no rules endpoint at all yet.
      // The dash says "not known", which is true.
      render: () => (
        <span className="categories__meta" title="Auto-rules are not built yet">
          —
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: 'minmax(0, 0.8fr)',
      // Two of the design's three states. `Unused` is deliberately absent: it
      // is defined as a lifetime usage count of zero (Q4) and no aggregate
      // exists, so every category that would be Unused reads Active here.
      render: (c) => (
        <span
          className={`categories__status categories__status--${c.is_active ? 'active' : 'archived'}`}
        >
          {c.is_active ? 'Active' : 'Archived'}
        </span>
      ),
    },
    {
      key: 'manage',
      header: 'Manage',
      width: '84px',
      align: 'end',
      // The design's capsule: rename, a hairline, and an overflow menu.
      //
      // A system category gets NEITHER — not Edit (the API answers a write
      // with 404) and not the menu, whose every entry (Archive, Merge) Q6
      // restricted to user categories. An empty capsule would be a control
      // group that does nothing, so the whole capsule is absent.
      render: (c) =>
        c.is_system ? null : (
          <span className="categories__manage">
            <button
              type="button"
              className="categories__manage-btn hoverable"
              style={{ minHeight: target, minWidth: target }}
              // "Edit", not the design's "Rename" tooltip: this opens the
              // full form — name, type, icon — and a control that says
              // Rename and then offers three fields has misdescribed itself.
              aria-label={`Edit ${c.name}`}
              onClick={() => onEdit(c)}
            >
              <PencilIcon />
            </button>
            <span className="categories__manage-rule" aria-hidden="true" />
            {/* Delete, not a `⋯` menu.
                
                The design draws an overflow menu, and this repo has no menu
                primitive — `AccountMenu` is not even a dropdown, and its own
                docblock argues the case: "a dropdown holding one item is a
                worse shape than a button." With Edit already on the pencil,
                that is exactly what this would be. Archive has no endpoint
                (Q5 asked for a dedicated one) and Merge has neither endpoint
                nor model support, so Delete is the only other real action.
                
                Building an accessible popup menu — focus management, Escape,
                outside-click, arrow keys, `role="menu"` — to hold one item is
                a new platform primitive, and a bigger decision than a feature
                commit should take. If Archive and Merge ever land, that is
                when a menu earns its place. */}
            <button
              type="button"
              className="categories__manage-btn categories__manage-btn--danger hoverable"
              style={{ minHeight: target, minWidth: target }}
              aria-label={`Delete ${c.name}`}
              onClick={() => onDelete(c)}
            >
              <TrashIcon />
            </button>
          </span>
        ),
    },
  ]

  // Category and Manage survive on a phone; Subs, Auto-rules and Status are
  // dropped rather than squeezed. Subs is recoverable by expanding the row,
  // Auto-rules has no data at all, and Status is Active on all but archived
  // rows — so the three that go are the three that cost least, and the name
  // gets the width back.
  const shown = phone ? columns.filter((c) => c.key === 'category' || c.key === 'manage') : columns

  const groups: Group<Category>[] = segments.map((segment) => ({
    label: CATEGORY_TYPE_LABEL[segment.type],
    // No `subtotalCents` — this table is not about money, and DataGrid renders
    // no amount for a group that declares none.
    rows: segment.nodes.map((n) => n.category),
  }))

  return (
    <>

      {/* The `<Panel>` moved here from `Categories.tsx` so the toolbar above
          stays on the CANVAS and the grid's header band is the first thing
          inside the card — the same arrangement `Merchants` uses. With the
          toolbar inside the panel the header bled to the card's edges while
          the controls above it sat 20px in, which read as a mistake. */}
      <Panel>
      <DataGrid
        columns={shown}
        groups={groups}
        rowKey={(c) => c.id}
        label="Categories"
        empty={`No category matches “${query}”.`}
        expansion={{
          canExpand: (c) => (childrenById.get(c.id)?.length ?? 0) > 0,
          isExpanded: (c) => open.has(c.id),
          onToggle: (c) => toggle(c.id),
          toggleLabel: (c) => `${open.has(c.id) ? 'Collapse' : 'Expand'} ${c.name}`,
          renderDetail: (c) => (
            <div className="categories__subs">
              {(childrenById.get(c.id) ?? []).map((child) => (
                <div key={child.id} className="categories__sub">
                  <span className="categories__cell">
                    <span className="categories__glyph">
                      <CategoryIcon category={child} />
                    </span>
                    <button
                      type="button"
                      className="categories__cell-name hoverable"
                      style={{ minHeight: target, minWidth: target }}
                      onClick={() => onActivate(child)}
                    >
                      {child.name}
                    </button>
                  </span>
                  {/* Editing and removing are real actions for a user subcategory
                      and nothing for a system one (the API 404s a write to it). */}
                  {!child.is_system && (
                    <span className="categories__sub-actions">
                      <button
                        type="button"
                        className="categories__sub-action hoverable"
                        style={{ minHeight: target, minWidth: target }}
                        aria-label={`Edit ${child.name}`}
                        onClick={() => onEdit(child)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="categories__sub-action hoverable"
                        style={{ minHeight: target, minWidth: target }}
                        aria-label={`Delete ${child.name}`}
                        onClick={() => onDelete(child)}
                      >
                        Delete
                      </button>
                    </span>
                  )}
                </div>
              ))}
            </div>
          ),
        }}
      />
      </Panel>
    </>
  )
}
